import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  GlobalStyles
} from '@mui/material';
import dayjs from 'dayjs';

import CallListViewV2 from './CallListViewV2';
import CallsToolbar from './CallsToolbar';
import { useCall } from '../../contexts/CallContext';
import { useAuth } from '../../contexts/AuthContext';
import Pagination from '../../components/common/Pagination';
import { fetchCallsDefaultFilters } from "../../services/common"
import api from '../../services/api';


const defaultDateRange = {
  filter: { id: 'All', label: 'All' },
  currentStart: null,
  currentEnd: null,
  compareStart: null,
  compareEnd: null,
  all_results: true,
};

// Helper function to parse filters from URL
const parseFiltersFromURL = (searchParams) => {
  const filters = {};
  
  // Parse array filters
  const arrayFilters = [
    'agent_id',
    'agent_ids',
    'lead_id',
    'lead_ids',
    'score',
    'call_direction',
    'status'
  ];
  
  arrayFilters.forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      // Split by comma and convert to appropriate type
      const values = value.split(',');
      // For numeric IDs, convert to numbers
      if (key.includes('_id') || key === 'score') {
        filters[key] = values.map(v => isNaN(v) ? v : Number(v));
      } else {
        filters[key] = values;
      }
    }
  });
  
  // Parse budget filter
  const budgetMin = searchParams.get('budget_min');
  const budgetMax = searchParams.get('budget_max');
  if (budgetMin || budgetMax) {
    filters.budget = {
      min_value: budgetMin ? parseInt(budgetMin) : undefined,
      max_value: budgetMax ? parseInt(budgetMax) : undefined,
    };
  }
  
  // Parse boolean filters
  const unassignedLead = searchParams.get('unassigned_lead');
  if (unassignedLead === 'true') {
    filters.unassigned_lead = true;
  }
  
  const leadStage = searchParams.get('lead__stage');
  if (leadStage) {
    filters.lead__stage = leadStage.split(',');
  }
  
  // Parse lead_type filter
  const leadType = searchParams.get('lead_type');
  if (leadType && leadType !== 'all') {
    filters.lead_type = leadType;
  }

  if (searchParams.get('unlinked') === 'true') {
    filters.unlinked = true;
  }

  return filters;
};

// Helper function to add filters to URL
const addFiltersToURL = (params, filters) => {
  if (!filters) return;
  
  Object.keys(filters).forEach(key => {
    const value = filters[key];
    
    if (Array.isArray(value) && value.length > 0) {
      // Convert array to comma-separated string
      params.set(key, value.join(','));
    } else if (key === 'budget' && value) {
      if (value.min_value) params.set('budget_min', value.min_value);
      if (value.max_value) params.set('budget_max', value.max_value);
    } else if (key === 'unassigned_lead' && value === true) {
      params.set('unassigned_lead', 'true');
    } else if (key === 'unlinked' && value === true) {
      params.set('unlinked', 'true');
    } else if (key === 'lead_type' && value && value !== 'all') {
      // Only add lead_type if it's not 'all'
      params.set('lead_type', value);
    } else if (value !== null && value !== undefined && value !== '' && key !== 'lead_type') {
      params.set(key, value);
    }
  });
};

const Calls = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDetailsRoute = /^\/calls\/\d+$/.test(location.pathname);

  // Get context data
  const {
    calls,
    fetchCalls,
    setAppliedFilters,
    appliedFilters,
    selectedStatus,
    setSelectedStatus,
    fetchStatusCounts,
    statusCounts,
    setStatusCounts,
    loading
  } = useCall();

  const { user } = useAuth();

  // Initialize state from URL params
  const getInitialPage = () => parseInt(searchParams.get('page')) || 1;
  const getInitialPageSize = () => parseInt(searchParams.get('page_size')) || 50;
  const getInitialSearch = () => searchParams.get('search') || '';
  const getInitialSortField = () => searchParams.get('sort_field') || 'date_time';
  const getInitialSortOrder = () => searchParams.get('sort_order') || 'desc';
  const getInitialStatus = () => searchParams.get('status') || 'all';

  // State management
  const [dateRangeState, setDateRangeState] = useState(defaultDateRange);
  
  // Pagination state - using 1-based page numbers
  const [page, setPage] = useState(getInitialPage);
  const [rowsPerPage, setRowsPerPage] = useState(getInitialPageSize);
  
  // Sorting state
  const [sortField, setSortField] = useState(getInitialSortField);
  const [sortOrder, setSortOrder] = useState(getInitialSortOrder);
  
  // Search state
  const [searchKeyword, setSearchKeyword] = useState(getInitialSearch);
  const [isSearchActive, setIsSearchActive] = useState(!!getInitialSearch());
  const [isSearching, setIsSearching] = useState(false);

  const [defaultFilters, setDefaultFilters] = useState({});
  const [isFilterUpdateInProgress, setIsFilterUpdateInProgress] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);

  // Helper function to format numbers like Google (1,234 results)
  const formatResultsCount = (count) => {
    return count.toLocaleString();
  };

  // Function to update URL with current state
  const updateURL = useCallback((params = {}) => {
    const urlParams = new URLSearchParams();
    
    // Add pagination
    const currentPage = params.page !== undefined ? params.page : page;
    const currentPageSize = params.pageSize !== undefined ? params.pageSize : rowsPerPage;
    if (currentPage > 1) urlParams.set('page', currentPage);
    if (currentPageSize !== 50) urlParams.set('page_size', currentPageSize);
    
    // Add search
    const currentSearch = params.search !== undefined ? params.search : searchKeyword;
    if (currentSearch) urlParams.set('search', currentSearch);
    
    // Add sorting
    const currentSortField = params.sortField !== undefined ? params.sortField : sortField;
    const currentSortOrder = params.sortOrder !== undefined ? params.sortOrder : sortOrder;
    if (currentSortField !== 'date_time') urlParams.set('sort_field', currentSortField);
    if (currentSortOrder !== 'desc') urlParams.set('sort_order', currentSortOrder);
    
    // Add sidebar selected state (separate from status filter to avoid collision)
    const currentStatus = params.status !== undefined ? params.status : selectedStatus;
    if (currentStatus && currentStatus !== 'all') urlParams.set('sidebar', currentStatus);
    
    // Add date range
    const currentDateRange = params.dateRange !== undefined ? params.dateRange : dateRangeState;
    if (currentDateRange) {
      // Add filter preset if not 'All'
      if (currentDateRange.filter?.id && currentDateRange.filter.id !== 'All') {
        urlParams.set('date_filter', currentDateRange.filter.id);
      }
      
      // Add specific dates if available and not "All"
      if (currentDateRange.currentStart && currentDateRange.currentEnd && !currentDateRange.all_results) {
        urlParams.set('start_date', currentDateRange.currentStart.format('YYYY-MM-DD'));
        urlParams.set('end_date', currentDateRange.currentEnd.format('YYYY-MM-DD'));
      }
    }
    
    // Add filters
    const currentFilters = params.filters !== undefined ? params.filters : appliedFilters;
    addFiltersToURL(urlParams, currentFilters);
    
    setSearchParams(urlParams, { replace: true });
  }, [page, rowsPerPage, searchKeyword, sortField, sortOrder, selectedStatus, dateRangeState, appliedFilters, setSearchParams]);

  // Unified function to build API parameters
  const buildApiParams = useCallback((searchTerm = null, customFilters = null, customDateRange = null, customPage = null, customPageSize = null, customSortField = null, customSortOrder = null) => {
    const currentSortField = customSortField || sortField;
    const currentSortOrder = customSortOrder || sortOrder;
    
    const params = {
      // Pagination
      page: customPage || page,
      page_size: customPageSize || rowsPerPage,
      // Sorting
      sort_field: currentSortField,
      sort_order: currentSortOrder
    };

    // Add search if provided
    if (searchTerm) {
      params.search = searchTerm;
    }

    // Add filters
    const filters = customFilters || appliedFilters;
    if (filters && typeof filters === 'object') {
      Object.keys(filters).forEach(key => {
        // Handle boolean values and other valid values
        if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
          params[key] = filters[key];
        } else if (typeof filters[key] === 'boolean') {
          // Always include boolean values (true/false)
          params[key] = filters[key];
        }
      });
    }

    // Handle all_results flag from date range filter
    const dateRange = customDateRange || dateRangeState;
    if (dateRange && dateRange.all_results !== undefined) {
      params.all_results = dateRange.all_results;
    } else if (dateRange && dateRange.filter?.id === 'All') {
      // Fallback: explicitly indicate "All" filter to backend
      params.all_results = true;
    }
    
    // Add date range if provided and not "All" results
    if (dateRange && dateRange.currentStart && dateRange.currentEnd && !params.all_results) {
      params.date_range = {
        start_date: dateRange.currentStart.format('YYYY-MM-DD'),
        end_date: dateRange.currentEnd.format('YYYY-MM-DD')
      };
    }

    return params;
  }, [page, rowsPerPage, appliedFilters, dateRangeState, sortField, sortOrder]);

  // Unified fetch function
  const fetchCallsData = useCallback((searchTerm = null, customFilters = null, customDateRange = null, customPage = null, customPageSize = null, forceRefresh = false) => {
    const params = buildApiParams(searchTerm, customFilters, customDateRange, customPage, customPageSize);
    return fetchCalls(params, customDateRange || dateRangeState, searchTerm, forceRefresh);
  }, [buildApiParams, fetchCalls, dateRangeState]);

  // Handle column sorting
  const handleSort = useCallback((field) => {
    const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newOrder);
    setPage(1); // Reset to first page when sorting changes
    
    // Update URL
    updateURL({ page: 1, sortField: field, sortOrder: newOrder });
    
    // Build params with new sort values
    const params = buildApiParams(searchKeyword, appliedFilters, dateRangeState, 1, rowsPerPage, field, newOrder);
    fetchCalls(params, dateRangeState, searchKeyword, true);
  }, [sortField, sortOrder, searchKeyword, appliedFilters, dateRangeState, rowsPerPage, buildApiParams, fetchCalls, updateURL]);

  // Handle date range change from DateRangeFilter
  const handleDateRangeChange = useCallback((rangeObj) => {
    const oldFilterId = dateRangeState?.filter?.id;
    const newFilterId = rangeObj?.filter?.id;
  
    if (
      oldFilterId !== newFilterId ||
      dateRangeState.currentStart?.format('YYYY-MM-DD') !== rangeObj.currentStart?.format('YYYY-MM-DD') ||
      dateRangeState.currentEnd?.format('YYYY-MM-DD') !== rangeObj.currentEnd?.format('YYYY-MM-DD')
    ) {
      setDateRangeState(rangeObj);
      // Update URL with new date range
      updateURL({ dateRange: rangeObj, page: 1 });
      setPage(1); // Reset to page 1 when date range changes
    }
  }, [dateRangeState, updateURL]);

  // Search submit handler for Enter key
  const handleSearchSubmit = (searchTerm) => {
    if (searchTerm.trim().length >= 3) {
      setIsSearchActive(true);
      setIsSearching(true);
      setIsFilterUpdateInProgress(true);
      setPage(1);
      setSearchKeyword(searchTerm);
      
      // Update URL
      updateURL({ page: 1, search: searchTerm });
      
      fetchCallsData(searchTerm, null, null, 1, null).finally(() => {
        setIsSearching(false);
        setIsFilterUpdateInProgress(false);
      });
    }
  };

  // Pagination handlers - simplified like Leads.js
  const handleChangePage = (newPage) => {
    // Ensure page is within valid range [1, total_pages]
    const maxPage = Math.ceil((calls?.pagination?.total_count || 0) / rowsPerPage) || 1;
    const validPage = Math.max(1, Math.min(newPage, maxPage));
    
    // Only proceed if the page actually changes and is valid
    if (validPage !== page && validPage >= 1 && validPage <= maxPage) {
      setPage(validPage);
      
      // Update URL
      updateURL({ page: validPage });
      
      // Use unified fetch function - directly fetch without triggering other effects
      const searchParam = searchKeyword.trim() ? searchKeyword : null;
      fetchCallsData(searchParam, null, null, validPage, null);
    }
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to page 1 (1-based)
    
    // Update URL
    updateURL({ page: 1, pageSize: newRowsPerPage });
    
    // Use unified fetch function
    const searchParam = searchKeyword.trim() ? searchKeyword : null;
    fetchCallsData(searchParam, null, null, 1, newRowsPerPage);
  };

  // Separate useEffect for status counts - only when date range changes
  useEffect(() => {
    if (dateRangeState) {
      // Pass appliedFilters as the second parameter to get filtered status counts
      fetchStatusCounts(dateRangeState, appliedFilters).then(setStatusCounts);
    }
  }, [dateRangeState, appliedFilters]); // Depend on both dateRangeState and appliedFilters


  // Function to clear applied filters and fetch all calls
  const handleResetFilters = useCallback(() => {
    setAppliedFilters({}); // Clear filters in context
    setSelectedStatus('all'); // Reset sidebar selection to 'all'
    setSearchKeyword(''); // Clear search
    setIsSearchActive(false); // Reset search state
    setPage(1);
    
    // Update URL - clear all params
    updateURL({ page: 1, search: '', filters: {}, status: 'all' });
    
    // Use the unified fetch function with reset parameters
    fetchCallsData(null, {}, dateRangeState, 1, rowsPerPage, true);
    
  }, [setAppliedFilters, setSelectedStatus, dateRangeState, fetchCallsData, rowsPerPage, updateURL]);

  // Instant-apply handler for toolbar quick filters.
  // Sets isFilterUpdateInProgress to block the generic effect, then does an explicit
  // forceRefresh fetch so the dedup check is bypassed.
  const handleQuickFilterChange = useCallback((newFilters) => {
    setAppliedFilters(newFilters);
    setPage(1);
    setIsFilterUpdateInProgress(true);
    updateURL({ page: 1, filters: newFilters });
    fetchStatusCounts(dateRangeState, newFilters).then(setStatusCounts);
    const params = buildApiParams(searchKeyword || null, newFilters, dateRangeState, 1, rowsPerPage);
    fetchCalls(params, dateRangeState, searchKeyword || null, true)
      .finally(() => setIsFilterUpdateInProgress(false));
  }, [setAppliedFilters, buildApiParams, searchKeyword, dateRangeState, rowsPerPage, fetchCalls, fetchStatusCounts, setStatusCounts, updateURL]);

  // Parse URL params on mount and initialize filters
  useEffect(() => {
    if (isInitialMount) {
      const urlFilters = parseFiltersFromURL(searchParams);
      const urlStatus = searchParams.get('sidebar') || 'all';

      // Parse date range from URL
      const dateFilter = searchParams.get('date_filter');
      const startDate = searchParams.get('start_date');
      const endDate = searchParams.get('end_date');

      if (dateFilter || (startDate && endDate)) {
        const newDateRange = { ...defaultDateRange };
        
        if (dateFilter) {
          newDateRange.filter = { id: dateFilter, label: dateFilter };
          newDateRange.all_results = false;
        }
        
        if (startDate && endDate) {
          newDateRange.currentStart = dayjs(startDate);
          newDateRange.currentEnd = dayjs(endDate);
          newDateRange.all_results = false;
        }
        
        setDateRangeState(newDateRange);
      }
      
      // Only update if there are filters in the URL
      if (Object.keys(urlFilters).length > 0) {
        setAppliedFilters(urlFilters);
      }
      
      // Set status from URL
      if (urlStatus !== 'all') {
        setSelectedStatus(urlStatus);
      }
      
      setIsInitialMount(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for URL changes (browser back/forward navigation)
  // This effect should ONLY respond to URL changes, not state changes
  useEffect(() => {
    if (!isInitialMount) {
      const urlPage = parseInt(searchParams.get('page')) || 1;
      const urlPageSize = parseInt(searchParams.get('page_size')) || 50;
      const urlSearch = searchParams.get('search') || '';
      const urlSortField = searchParams.get('sort_field') || 'date_time';
      const urlSortOrder = searchParams.get('sort_order') || 'desc';
      const urlStatus = searchParams.get('sidebar') || 'all';
      const urlFilters = parseFiltersFromURL(searchParams);
      
      // Parse date range from URL
      const dateFilter = searchParams.get('date_filter');
      const startDate = searchParams.get('start_date');
      const endDate = searchParams.get('end_date');
      
      // Check if date range in URL differs from current state
      const currentDateFilter = dateRangeState?.filter?.id;
      const currentStartDate = dateRangeState?.currentStart?.format('YYYY-MM-DD');
      const currentEndDate = dateRangeState?.currentEnd?.format('YYYY-MM-DD');
      
      if (dateFilter !== currentDateFilter || startDate !== currentStartDate || endDate !== currentEndDate) {
        const newDateRange = { ...defaultDateRange };
        
        if (dateFilter) {
          newDateRange.filter = { id: dateFilter, label: dateFilter };
          newDateRange.all_results = false;
        }
        
        if (startDate && endDate) {
          newDateRange.currentStart = dayjs(startDate);
          newDateRange.currentEnd = dayjs(endDate);
          newDateRange.all_results = false;
        }
        
        if (dateFilter || (startDate && endDate)) {
          setDateRangeState(newDateRange);
        } else if (!dateFilter && !startDate && !endDate && currentDateFilter !== 'All') {
          // Reset to default if no date params in URL
          setDateRangeState(defaultDateRange);
        }
      }
      
      // Update state if URL params differ from current state
      // Use refs to avoid circular dependencies
      if (urlPage !== page) setPage(urlPage);
      if (urlPageSize !== rowsPerPage) setRowsPerPage(urlPageSize);
      if (urlSearch !== searchKeyword) {
        setSearchKeyword(urlSearch);
        setIsSearchActive(!!urlSearch);
      }
      if (urlSortField !== sortField) setSortField(urlSortField);
      if (urlSortOrder !== sortOrder) setSortOrder(urlSortOrder);
      if (urlStatus !== selectedStatus) setSelectedStatus(urlStatus);
      if (JSON.stringify(urlFilters) !== JSON.stringify(prevFiltersRef.current)) {
        setAppliedFilters(urlFilters);
        prevFiltersRef.current = urlFilters;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isInitialMount]);

  // Fetch default filters data when component mounts
  useEffect(() => {
    fetchCallsDefaultFilters().then(data => {
      setDefaultFilters(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search effect - only handle search reset when keyword is cleared
  useEffect(() => {
    // Only handle search reset when keyword is cleared
    if (searchKeyword.length === 0 && isSearchActive) {
      setIsSearchActive(false);
      setIsSearching(false);
      setPage(1); // Reset to page 1
      
      // Update URL
      updateURL({ page: 1, search: '' });
      
      fetchCallsData(null, null, null, 1, null, true);
    }
  }, [searchKeyword, isSearchActive, fetchCallsData, updateURL]);

  // Track previous filters to detect actual filter changes
  const prevFiltersRef = useRef(appliedFilters);
  const prevStatusRef = useRef(selectedStatus);

  // Update URL when filters or status change (but not on initial mount)
  useEffect(() => {
    if (!isInitialMount) {
      // Check if filters or status actually changed
      const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(appliedFilters);
      const statusChanged = prevStatusRef.current !== selectedStatus;
      
      if (filtersChanged || statusChanged) {
        // Reset to page 1 when filters change
        setPage(1);
        updateURL({ page: 1, filters: appliedFilters, status: selectedStatus });
        
        // Update refs
        prevFiltersRef.current = appliedFilters;
        prevStatusRef.current = selectedStatus;
      }
    }
  }, [appliedFilters, selectedStatus, isInitialMount, updateURL]);

  // Main data fetching effect - handles filters and date range changes (NOT pagination).
  // Do not gate on isSearchActive: the toolbar sets that for any non-empty search text, which
  // would otherwise block refetches when the user changes sidebar / date range while searching.
  useEffect(() => {
    if (isFilterUpdateInProgress || isInitialMount) {
      return;
    }

    if (!isDetailsRoute) {
      const searchParam = searchKeyword.trim() ? searchKeyword : null;
      fetchCallsData(searchParam, appliedFilters, dateRangeState, page, null);
    }
    // searchKeyword is read for the request body but omitted from deps: search-only updates are
    // fetched from CallsToolbar.onSearchChange to avoid double requests on every debounced keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, dateRangeState, page, isDetailsRoute, isFilterUpdateInProgress, isInitialMount, fetchCallsData]);


  const handleCallUpdated = (data) => {
    // Refresh the calls data after a call is marked as lead
    console.log('Call updated, refreshing data:', data);
    fetchCallsData(null, appliedFilters, dateRangeState, 1, rowsPerPage, true);
    
    // Also refresh status counts
    fetchStatusCounts(dateRangeState, appliedFilters).then(setStatusCounts);
  };

  // Export handler - similar to leads export
  const handleExport = useCallback(async () => {
    try {
      console.log('Starting calls export...');
      const params = buildApiParams(searchKeyword, appliedFilters, dateRangeState);
      params.is_export = true;
      console.log('Export payload:', params);
      
      const response = await api.post('/lms/calls/', params);
      console.log('Export response:', response.data);
      
      if (response.data && response.data.success) {
        const count = response.data.count || 0;
        const message = response.data.message || 'Export request received. You will receive an email with the CSV file shortly.';
        
        // Show success message
        alert(`✅ ${message}\n\nExport includes ${count} calls. Please check your email inbox.`);
      } else {
        console.error('Export failed: Invalid response', response.data);
        alert('Export failed: Invalid response from server');
      }
    } catch (err) {
      console.error('Export failed:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      alert(`Export failed: ${errorMessage}`);
    }
  }, [buildApiParams, searchKeyword, appliedFilters, dateRangeState]);

  return (
    <>
      <GlobalStyles
        styles={{
          '@keyframes pulse': {
            '0%': {
              opacity: 1,
            },
            '50%': {
              opacity: 0.5,
            },
            '100%': {
              opacity: 1,
            },
          },
        }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {/* Toolbar on top — same layout as Leads */}
        <CallsToolbar
            searchKeyword={searchKeyword}
            onSearchChange={(value) => {
              const term = typeof value === 'string' ? value.trim() : '';
              setIsSearchActive(!!term);
              setIsSearching(!!term);
              setPage(1);
              setSearchKeyword(term);
              updateURL({ page: 1, search: term || '' });
              fetchCallsData(term || null, appliedFilters, dateRangeState, 1, rowsPerPage).finally(() => {
                setIsSearching(false);
              });
            }}
            onResetFilters={handleResetFilters}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalCount={calls?.pagination?.total_count || 0}
            dateRangeState={dateRangeState}
            onDateRangeChange={handleDateRangeChange}
            filterOptions={defaultFilters}
            appliedFilters={appliedFilters}
            onQuickFilterChange={handleQuickFilterChange}
          />

          {/* Sidebar | Table inline (CallListViewV2) */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 1 }}>
            {loading?.calls ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <Typography>Loading calls...</Typography>
              </Box>
            ) : (
              <CallListViewV2
                calls={calls}
                page={page}
                rowsPerPage={rowsPerPage}
                searchKeyword={searchKeyword}
                isSearchActive={isSearchActive}
                selectedStatus={selectedStatus}
                onCallUpdated={handleCallUpdated}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleSort}
                statusCounts={statusCounts}
                user={user}
                sidebarWidth={200}
                onExport={handleExport}
              />
            )}
          </Box>
        </Box>

    </>
  );
};

export default Calls;